import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Body,
	Document,
	Head,
	Page,
	render as renderUseprint,
	Tailwind,
} from "@useprint/components";
import {
	ArrowRight,
	Check,
	CheckCircle2,
	ChevronDown,
	Copy,
	Download,
	Loader2,
	Sparkles,
} from "lucide-react";
import * as React from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
	oneDark,
	oneLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resolveServerBaseURL } from "@/lib/server-url";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/")({
	component: LandingPage,
});

const MAX_TEXT_LENGTH = 100;
const MIN_TEXT_LENGTH = 8;
const DEFAULT_TEXT = "Tailwind classes can shape this exact PDF.";

const toneOptions = [
	{
		value: "subtle",
		label: "Subtle",
		cardClassName: "border-border bg-card",
		badgeClassName:
			"border border-border bg-secondary text-secondary-foreground",
		buttonVariant: "outline" as const,
		buttonClassName: "bg-background",
		headingClassName: "text-foreground",
		bodyClassName: "text-muted-foreground",
	},
	{
		value: "strong",
		label: "Strong",
		cardClassName: "border-primary/25 bg-secondary",
		badgeClassName: "bg-primary text-primary-foreground",
		buttonVariant: "default" as const,
		buttonClassName: "shadow-sm",
		headingClassName: "text-foreground",
		bodyClassName: "text-muted-foreground",
	},
	{
		value: "muted",
		label: "Muted",
		cardClassName: "border-border bg-muted/60",
		badgeClassName: "border border-border bg-background text-muted-foreground",
		buttonVariant: "secondary" as const,
		buttonClassName: "border border-border",
		headingClassName: "text-foreground",
		bodyClassName: "text-muted-foreground",
	},
] as const;

const radiusOptions = [
	{ value: "rounded-none", label: "Square" },
	{ value: "rounded-md", label: "Tight" },
	{ value: "rounded-lg", label: "Soft" },
] as const;

const spacingOptions = [
	{
		value: "compact",
		label: "Compact",
		sectionClassName: "gap-4 p-6",
		stackClassName: "space-y-3",
	},
	{
		value: "relaxed",
		label: "Relaxed",
		sectionClassName: "gap-5 p-7",
		stackClassName: "space-y-4",
	},
	{
		value: "airy",
		label: "Airy",
		sectionClassName: "gap-6 p-8",
		stackClassName: "space-y-5",
	},
] as const;

const alignmentOptions = [
	{
		value: "left",
		label: "Left",
		containerClassName: "items-start text-left",
		rowClassName: "justify-start",
	},
	{
		value: "center",
		label: "Center",
		containerClassName: "items-center text-center",
		rowClassName: "justify-center",
	},
] as const;

const emphasisOptions = [
	{ value: "quiet", label: "Quiet", className: "shadow-none" },
	{ value: "bordered", label: "Bordered", className: "shadow-sm" },
	{
		value: "elevated",
		label: "Elevated",
		className: "shadow-[0_20px_60px_rgba(0,0,0,0.18)]",
	},
] as const;

const presetMap = {
	tone: Object.fromEntries(toneOptions.map((option) => [option.value, option])),
	radius: Object.fromEntries(
		radiusOptions.map((option) => [option.value, option]),
	),
	spacing: Object.fromEntries(
		spacingOptions.map((option) => [option.value, option]),
	),
	alignment: Object.fromEntries(
		alignmentOptions.map((option) => [option.value, option]),
	),
	emphasis: Object.fromEntries(
		emphasisOptions.map((option) => [option.value, option]),
	),
};

const trustedBy = [
	{
		name: "Solenergikvalitet",
		href: "https://solenergikvalitet.se",
		src: "/trusted-by-solenergikvalitet.svg",
		logoClassName: "h-9 sm:h-10",
	},
	{
		name: "Solkoll",
		href: "https://solkoll.se",
		src: "/trusted-by-solkoll.svg",
		logoClassName: "h-8 sm:h-9",
	},
] as const;

const faqItems = [
	{
		question: "Can users edit arbitrary code?",
		answer:
			"No. The landing demo only exposes a short text field and curated Tailwind presets.",
	},
	{
		question: "Does the PDF match the preview?",
		answer:
			"Yes. The page preview and exported PDF are rendered from the same React document config.",
	},
	{
		question: "Why limit the controls?",
		answer:
			"It keeps the public demo fast, cache-friendly, and safe while still proving full utility-class styling works.",
	},
] as const;

const packageManagerOptions = [
	{ value: "npm", label: "npm", command: "npm install useprint-js" },
	{ value: "pnpm", label: "pnpm", command: "pnpm add useprint-js" },
	{ value: "yarn", label: "yarn", command: "yarn add useprint-js" },
	{ value: "bun", label: "bun", command: "bun add useprint-js" },
] as const;

const quickStartSnippet = `import { usePrint } from "useprint-js";

const { print } = usePrint({ apiKey: process.env.USEPRINT_API_KEY! });

const pdf = await print(
\t<Document />,
\t{
\t\tpdfOptions: {
\t\t\tmargin: { top: 0, left: 0, right: 0, bottom: 0 },
\t\t\ttimeout: 10000,
\t\t},
\t},
);`;

type ToneValue = (typeof toneOptions)[number]["value"];
type RadiusValue = (typeof radiusOptions)[number]["value"];
type SpacingValue = (typeof spacingOptions)[number]["value"];
type AlignmentValue = (typeof alignmentOptions)[number]["value"];
type EmphasisValue = (typeof emphasisOptions)[number]["value"];
type PackageManagerValue = (typeof packageManagerOptions)[number]["value"];

type DemoConfig = {
	text: string;
	tone: ToneValue;
	radius: RadiusValue;
	spacing: SpacingValue;
	alignment: AlignmentValue;
	emphasis: EmphasisValue;
};

const defaultConfig: DemoConfig = {
	text: DEFAULT_TEXT,
	tone: "strong",
	radius: "rounded-none",
	spacing: "relaxed",
	alignment: "left",
	emphasis: "bordered",
};

function LandingPage() {
	const [config, setConfig] = React.useState<DemoConfig>(defaultConfig);
	const [runState, setRunState] = React.useState<
		"idle" | "rendering" | "complete" | "error"
	>("idle");
	const [runMessage, setRunMessage] = React.useState(
		"Change presets, then render a matching PDF.",
	);
	const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
	const [packageManager, setPackageManager] =
		React.useState<PackageManagerValue>("npm");
	const [copiedInstallCommand, setCopiedInstallCommand] = React.useState(false);
	const [copiedSnippet, setCopiedSnippet] = React.useState(false);
	const [isDarkMode, setIsDarkMode] = React.useState(() =>
		typeof document !== "undefined"
			? document.documentElement.classList.contains("dark")
			: false,
	);

	const trimmedText = config.text.trim();
	const textLength = config.text.length;
	const textError =
		trimmedText.length < MIN_TEXT_LENGTH
			? `Use at least ${MIN_TEXT_LENGTH} characters.`
			: textLength > MAX_TEXT_LENGTH
				? `Keep it under ${MAX_TEXT_LENGTH} characters.`
				: null;
	const canGenerate = !textError && runState !== "rendering";
	const primaryActionDisabled =
		runState === "rendering" || (!pdfUrl && !canGenerate);
	const installCommand =
		packageManagerOptions.find((option) => option.value === packageManager)
			?.command ?? packageManagerOptions[0].command;

	React.useEffect(() => {
		return () => {
			if (pdfUrl) {
				URL.revokeObjectURL(pdfUrl);
			}
		};
	}, [pdfUrl]);

	React.useEffect(() => {
		if (!copiedInstallCommand) {
			return;
		}

		const timeout = window.setTimeout(
			() => setCopiedInstallCommand(false),
			1800,
		);
		return () => window.clearTimeout(timeout);
	}, [copiedInstallCommand]);

	React.useEffect(() => {
		if (!copiedSnippet) {
			return;
		}

		const timeout = window.setTimeout(() => setCopiedSnippet(false), 1800);
		return () => window.clearTimeout(timeout);
	}, [copiedSnippet]);

	React.useEffect(() => {
		if (typeof document === "undefined") {
			return;
		}

		const syncTheme = () => {
			setIsDarkMode(document.documentElement.classList.contains("dark"));
		};

		syncTheme();

		const observer = new MutationObserver(syncTheme);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["class"],
		});

		return () => observer.disconnect();
	}, []);

	const resolvedConfig = React.useMemo(
		() => ({
			...config,
			text: trimmedText || DEFAULT_TEXT,
		}),
		[config, trimmedText],
	);

	const updateConfig = <K extends keyof DemoConfig>(
		key: K,
		value: DemoConfig[K],
	) => {
		setConfig((current) => ({ ...current, [key]: value }));
	};

	const handleDownload = React.useCallback(() => {
		if (!pdfUrl) {
			return;
		}
		const link = document.createElement("a");
		link.href = pdfUrl;
		link.download = "useprint-demo.pdf";
		link.click();
	}, [pdfUrl]);

	const handleGenerate = React.useCallback(async () => {
		if (pdfUrl && runState === "complete") {
			handleDownload();
			return;
		}

		if (textError) {
			setRunState("error");
			setRunMessage("Shorten or expand the copy slightly before rendering.");
			toast.error(textError);
			return;
		}

		setRunState("rendering");
		setRunMessage(
			"Rendering the current Tailwind preset combination to PDF...",
		);

		try {
			const html = await renderUseprint(
				<LandingDemoDocument config={resolvedConfig} />,
			);
			const response = await fetch(
				`${resolveServerBaseURL()}/pdf/watermarked`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						html,
						pdfOptions: {
							format: "A4",
							margin: {
								top: 18,
								right: 18,
								bottom: 18,
								left: 18,
							},
						},
					}),
				},
			);

			if (!response.ok) {
				const text = await response.text().catch(() => "");
				throw new Error(text || "Failed to generate the watermarked PDF.");
			}

			const blob = await response.blob();
			setPdfUrl((existingUrl) => {
				if (existingUrl) {
					URL.revokeObjectURL(existingUrl);
				}
				return URL.createObjectURL(blob);
			});
			setRunState("complete");
			setRunMessage("The live preview and exported PDF are now in sync.");
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "Failed to generate the watermarked PDF.";
			setRunState("error");
			setRunMessage("The render failed before a PDF could be returned.");
			toast.error(message);
		}
	}, [handleDownload, pdfUrl, resolvedConfig, runState, textError]);

	const handleCopyInstallCommand = React.useCallback(async () => {
		try {
			await navigator.clipboard.writeText(installCommand);
			setCopiedInstallCommand(true);
		} catch {
			// Ignore clipboard failures here since the button text is the only intended feedback.
		}
	}, [installCommand]);

	const handleCopySnippet = React.useCallback(async () => {
		try {
			await navigator.clipboard.writeText(quickStartSnippet);
			setCopiedSnippet(true);
		} catch {
			// Ignore clipboard failures here since the button text is the only intended feedback.
		}
	}, []);

	return (
		<div className="min-h-[calc(100svh-58px)] bg-background text-foreground">
			<main className="mx-auto flex max-w-6xl flex-col gap-16 px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
				<section className="space-y-10 pt-2">
					<div className="space-y-6">
						<h1 className="max-w-4xl font-semibold text-4xl tracking-tight sm:text-6xl lg:text-7xl">
							Write React <br />
							Export as PDF
						</h1>
						<p className="max-w-3xl text-lg text-muted-foreground leading-8">
							The DX for creating PDF files has never been easier.
						</p>
						<div className="flex flex-wrap items-center gap-3">
							<Button size="lg" variant="default" asChild>
								<a
									href="https://github.com/maxscn/useprint"
									target="_blank"
									rel="noreferrer"
								>
									View on GitHub
								</a>
							</Button>
							<Button size="lg" variant="secondary" asChild>
								<Link to="/docs">Read the docs</Link>
							</Button>
						</div>
						<div className="space-y-4 pt-2">
							<p className="text-muted-foreground text-sm">Trusted by</p>
							<div className="grid max-w-2xl gap-3 sm:grid-cols-2">
								{trustedBy.map((brand) => (
									<a
										key={brand.name}
										href={brand.href}
										target="_blank"
										rel="noreferrer"
										aria-label={brand.name}
										className="flex min-h-20 items-center justify-center rounded-none border border-border/40 bg-transparent px-5 py-4 transition-colors hover:border-primary/30 hover:bg-transparent"
									>
										<img
											src={brand.src}
											alt={`${brand.name} logo`}
											className={cn(
												"w-full max-w-[190px] object-contain",
												brand.logoClassName,
											)}
											loading="lazy"
										/>
									</a>
								))}
							</div>
						</div>
						<div className="space-y-4 pt-3">
							<div className="overflow-hidden rounded-none border border-border bg-card">
								<div className="flex flex-wrap items-center justify-between gap-2 border-border border-b px-4 py-3">
									<div className="min-w-0 text-muted-foreground text-sm">
										${" "}
										<code className="font-mono text-foreground">
											{installCommand}
										</code>
									</div>
									<div className="flex items-center gap-2">
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="outline" size="sm">
													{packageManager}
													<ChevronDown className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												{packageManagerOptions.map((option) => (
													<DropdownMenuItem
														key={option.value}
														onClick={() => setPackageManager(option.value)}
														className="justify-between gap-6"
													>
														<span>{option.label}</span>
														{packageManager === option.value ? (
															<Check className="h-4 w-4 text-primary" />
														) : null}
													</DropdownMenuItem>
												))}
											</DropdownMenuContent>
										</DropdownMenu>
										<Button
											variant="outline"
											size="sm"
											onClick={handleCopyInstallCommand}
										>
											{copiedInstallCommand ? (
												<>
													<Check className="h-4 w-4" />
													Copied
												</>
											) : (
												<>
													<Copy className="h-4 w-4" />
													Copy
												</>
											)}
										</Button>
									</div>
								</div>
							</div>
							<div className="overflow-hidden rounded-none border border-border bg-card">
								<div className="flex flex-wrap items-center justify-between gap-2 border-border border-b px-4 py-3">
									<div className="min-w-0">
										<p className="font-medium text-foreground text-sm">
											Quick start
										</p>
										<p className="text-muted-foreground text-sm">
											Drop this in right after install.
										</p>
									</div>
									<Button
										variant="outline"
										size="sm"
										onClick={handleCopySnippet}
									>
										{copiedSnippet ? (
											<>
												<Check className="h-4 w-4" />
												Copied
											</>
										) : (
											<>
												<Copy className="h-4 w-4" />
												Copy
											</>
										)}
									</Button>
								</div>
								<SyntaxHighlighter
									language="tsx"
									style={isDarkMode ? oneDark : oneLight}
									customStyle={{
										margin: 0,
										borderRadius: 0,
										background: "transparent",
										padding: "1.25rem",
										fontSize: "0.92rem",
										lineHeight: 1.7,
									}}
									codeTagProps={{
										style: {
											fontFamily: '"JetBrains Mono Variable", monospace',
										},
									}}
									wrapLongLines
								>
									{quickStartSnippet}
								</SyntaxHighlighter>
							</div>
						</div>
					</div>

					<Card className="gap-0 overflow-hidden border-border bg-card py-0 shadow-sm">
						<CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1.25fr)_420px]">
							<div className="flex min-h-[280px] flex-col lg:min-h-0 h-full border-border border-b bg-muted/20 p-5 lg:border-r lg:border-b-0 lg:p-8">
								<div className="mb-4 flex shrink-0 items-center justify-between gap-3">
									<div>
										<p className="font-medium text-foreground text-sm">
											Preview
										</p>
										<p className="text-muted-foreground text-sm">
											The exported PDF uses this same component tree.
										</p>
									</div>
									{pdfUrl ? (
										<Button
											variant="outline"
											size="sm"
											onClick={handleDownload}
										>
											<Download className="h-4 w-4" />
											Download
										</Button>
									) : null}
								</div>
								<div className="light flex min-h-0 flex-1 flex-col rounded-none border border-border bg-background p-4 sm:p-6">
									<LandingPreview config={resolvedConfig} />
								</div>
							</div>

							<div className="space-y-6 p-5 lg:p-6">
								<div>
									<p className="font-medium text-foreground text-sm">
										Settings
									</p>
									<p className="mt-1 text-muted-foreground text-sm">
										Update the presets, then generate a matching PDF.
									</p>
								</div>

								<div className="space-y-2">
									<Label htmlFor="demo-text">Short message</Label>
									<Input
										id="demo-text"
										value={config.text}
										onChange={(event) =>
											updateConfig("text", event.target.value)
										}
										maxLength={MAX_TEXT_LENGTH}
										placeholder="Keep it short and specific"
										aria-invalid={Boolean(textError)}
									/>
									<div className="flex items-center justify-between text-muted-foreground text-xs">
										<span>
											{textError ??
												"Public demo input is capped at 100 characters."}
										</span>
										<span>
											{textLength}/{MAX_TEXT_LENGTH}
										</span>
									</div>
								</div>

								<div className="grid gap-4 sm:grid-cols-2">
									<PresetSelect
										id="demo-tone"
										label="Tone"
										value={config.tone}
										onChange={(value) =>
											updateConfig("tone", value as ToneValue)
										}
										options={toneOptions}
									/>
									<PresetSelect
										id="demo-radius"
										label="Radius"
										value={config.radius}
										onChange={(value) =>
											updateConfig("radius", value as RadiusValue)
										}
										options={radiusOptions}
									/>
									<PresetSelect
										id="demo-spacing"
										label="Spacing"
										value={config.spacing}
										onChange={(value) =>
											updateConfig("spacing", value as SpacingValue)
										}
										options={spacingOptions}
									/>
									<PresetSelect
										id="demo-alignment"
										label="Alignment"
										value={config.alignment}
										onChange={(value) =>
											updateConfig("alignment", value as AlignmentValue)
										}
										options={alignmentOptions}
									/>
								</div>

								<PresetSelect
									id="demo-emphasis"
									label="Card emphasis"
									value={config.emphasis}
									onChange={(value) =>
										updateConfig("emphasis", value as EmphasisValue)
									}
									options={emphasisOptions}
								/>

								<Button
									className="w-full"
									onClick={handleGenerate}
									disabled={primaryActionDisabled}
								>
									{runState === "rendering" ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin" />
											Rendering PDF
										</>
									) : pdfUrl && runState === "complete" ? (
										<>
											<Download className="h-4 w-4" />
											Download
										</>
									) : (
										<>
											Generate PDF
											<ArrowRight className="h-4 w-4" />
										</>
									)}
								</Button>

								<div className="rounded-none border border-border bg-secondary/40 px-4 py-3 text-muted-foreground text-sm">
									<div className="flex items-start gap-3">
										{runState === "rendering" ? (
											<Loader2 className="mt-0.5 h-4 w-4 animate-spin text-primary" />
										) : runState === "complete" ? (
											<CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
										) : (
											<Sparkles className="mt-0.5 h-4 w-4 text-primary" />
										)}
										<div>
											<p className="font-medium text-foreground">
												{runMessage}
											</p>
										</div>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</section>

				<section className="grid gap-8 border-border border-t pt-14 lg:grid-cols-[minmax(0,1fr)_320px]">
					<div className="space-y-5">
						<h2 className="font-semibold text-3xl tracking-tight sm:text-4xl">
							What is usePrint?
						</h2>
						<p className="max-w-3xl text-base text-muted-foreground leading-8 sm:text-lg">
							usePrint lets you build document layouts in React, style them with
							Tailwind utilities, and render the result as production-ready
							PDFs.
						</p>
						<p className="max-w-3xl text-base text-muted-foreground leading-8 sm:text-lg">
							This landing demo intentionally exposes only a small surface area,
							but the same rendering path supports richer components, spacing
							systems, typography, and regular CSS-driven design decisions.
						</p>
					</div>
				</section>

				<footer className="flex flex-col gap-3 border-border border-t pt-8 text-muted-foreground text-sm sm:flex-row sm:items-center sm:justify-between">
					<p>© 2026 usePrint</p>
					<div className="flex flex-wrap items-center gap-4">
						<Link to="/docs" className="hover:text-foreground">
							Docs
						</Link>
						<Link to="/login" className="hover:text-foreground">
							Log in
						</Link>
						<a
							href="https://github.com/maxscn/useprint"
							target="_blank"
							rel="noreferrer"
							className="hover:text-foreground"
						>
							GitHub
						</a>
					</div>
				</footer>
			</main>
		</div>
	);
}

function LandingPreview({ config }: { config: DemoConfig }) {
	const styles = getPresetStyles(config);

	return (
		<div className="mx-auto flex h-full min-h-0 w-full max-w-xl flex-1 flex-col">
			<Card
				className={cn(
					"flex h-full min-h-0 flex-1 flex-col gap-0 py-0 transition-all",
					styles.radiusClassName,
					styles.cardClassName,
					styles.sectionClassName,
					styles.emphasisClassName,
				)}
			>
				<CardContent className="flex min-h-0 flex-1 flex-col px-0">
					<div
						className={cn(
							"flex min-h-0 flex-1 flex-col",
							styles.containerClassName,
							styles.stackClassName,
						)}
					>
						<div
							className={cn(
								"inline-flex items-center rounded-full px-3 py-1 font-medium text-xs",
								styles.badgeClassName,
							)}
						>
							Tailwind preset demo
						</div>
						<div className={styles.stackClassName}>
							<h2
								className={cn(
									"max-w-xl font-semibold text-3xl tracking-tight",
									styles.headingClassName,
								)}
							>
								{config.text}
							</h2>
							<p
								className={cn(
									"max-w-xl text-sm leading-7",
									styles.bodyClassName,
								)}
							>
								Regular React structure plus curated Tailwind presets, exported
								through the same PDF renderer.
							</p>
						</div>
						<div
							className={cn(
								"mt-auto flex flex-wrap gap-3",
								styles.rowClassName,
							)}
						>
							<Button
								variant={styles.buttonVariant}
								className={styles.buttonClassName}
							>
								Generate PDF
							</Button>
							<Button variant="ghost">Preview state</Button>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function LandingDemoDocument({ config }: { config: DemoConfig }) {
	const styles = getPresetStyles(config);

	return (
		<Document>
			<Tailwind>
				<Head />
				<Body>
					<Page className="light flex min-h-[1122px] items-center justify-center bg-background px-8 py-10 text-foreground">
						<div className="w-full max-w-[720px]">
							<div
								className={cn(
									"flex flex-col border",
									styles.radiusClassName,
									styles.cardClassName,
									styles.sectionClassName,
									styles.emphasisClassName,
								)}
							>
								<div
									className={cn(
										"flex flex-col",
										styles.containerClassName,
										styles.stackClassName,
									)}
								>
									<div
										className={cn(
											"inline-flex items-center rounded-full px-3 py-1 font-medium text-xs",
											styles.badgeClassName,
										)}
									>
										Tailwind preset demo
									</div>
									<div className={styles.stackClassName}>
										<h1
											className={cn(
												"max-w-xl font-semibold text-4xl tracking-tight",
												styles.headingClassName,
											)}
										>
											{config.text}
										</h1>
										<p
											className={cn(
												"max-w-xl text-base leading-7",
												styles.bodyClassName,
											)}
										>
											The same React document config is used for the on-page
											preview and the exported PDF.
										</p>
									</div>
									<div
										className={cn("flex flex-wrap gap-3", styles.rowClassName)}
									>
										<div
											className={cn(
												"inline-flex items-center justify-center rounded-md px-4 py-2 font-medium text-sm",
												styles.buttonVariant === "default"
													? "bg-primary text-primary-foreground"
													: styles.buttonVariant === "secondary"
														? "border border-border bg-secondary text-secondary-foreground"
														: "border border-border bg-background text-foreground",
												styles.buttonClassName,
											)}
										>
											Generate PDF
										</div>
										<div className="inline-flex items-center justify-center rounded-md px-4 py-2 font-medium text-muted-foreground text-sm">
											Preview state
										</div>
									</div>
								</div>
							</div>
						</div>
					</Page>
				</Body>
			</Tailwind>
		</Document>
	);
}

function getPresetStyles(config: DemoConfig) {
	const tone = presetMap.tone[config.tone];
	const radius = presetMap.radius[config.radius];
	const spacing = presetMap.spacing[config.spacing];
	const alignment = presetMap.alignment[config.alignment];
	const emphasis = presetMap.emphasis[config.emphasis];

	return {
		badgeClassName: tone.badgeClassName,
		bodyClassName: tone.bodyClassName,
		buttonClassName: tone.buttonClassName,
		buttonVariant: tone.buttonVariant,
		cardClassName: tone.cardClassName,
		containerClassName: alignment.containerClassName,
		emphasisClassName: emphasis.className,
		headingClassName: tone.headingClassName,
		radiusClassName: radius.value,
		rowClassName: alignment.rowClassName,
		sectionClassName: spacing.sectionClassName,
		stackClassName: spacing.stackClassName,
	};
}

function PresetSelect({
	id,
	label,
	options,
	onChange,
	value,
}: {
	id: string;
	label: string;
	options: ReadonlyArray<{ label: string; value: string }>;
	onChange: (value: string) => void;
	value: string;
}) {
	return (
		<div className="space-y-2">
			<Label htmlFor={id}>{label}</Label>
			<select
				id={id}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none ring-offset-background focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
			>
				{options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</div>
	);
}
